import { FileExcelFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Modal, Row, Space, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch } from '../../../../app/hooks';
import { selectCommon, setModal } from '../../../../common/slices/commonSlice';
import { IPermission, crud } from '../../../../common/types/commonTypes';
import { debounce, filterNullColumns } from '../../../../common/utils/common.utils';
import { getRowHightLightClass } from '../../../../common/utils/rowColorChange';
import usePagination from '../../../../common/utils/usePagination';
import useUrlParams from '../../../../common/utils/useUrlParams';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { FormInput } from '../../../../components/common/FormItem/FormItems';
import Reload from '../../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import { parseDateRangeObject } from '../../../../helpers/healper';
import {
  useActivateClientMutation,
  useGetClientsV2Query,
  useLazyGetClientExcelDataQuery,
} from '../api/endpoints/clientEndpoints';
import ClientSmsModal from '../modals/ClientSmsModal';
import { ClientDataType } from '../types/clientInterfaces';
import { viewClientcolumns } from '../utils/utils_tsx/viewClientUtil';

const ClientLists = ({ permission }: { permission?: IPermission }) => {
  const { setUrlParam, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [editInfo, setEditInfo] = useState<ClientDataType | null>(null);
  const dispatch = useAppDispatch();
  const commonState = useSelector(selectCommon);

  const [fetchExcel] = useLazyGetClientExcelDataQuery();

  const {
    data,
    refetch: getAllRefetch,
    isLoading: dataIsLoading,
  } = useGetClientsV2Query({
    ...getAllUrlParams(),
    page,
    size,
  });

  const showModal = () => {
    dispatch(setModal(true));
  };

  const [activate, { isLoading }] = useActivateClientMutation();

  const handleModal = () => {
    dispatch(setModal(false));
  };

  const column = viewClientcolumns({
    setEditInfo,
    showModal,
    activate,
    loading: isLoading,
    permission,
    pagination,
  });
  // Add safety check for data mapping
  const dataWithKey =
    data?.data?.map((item, index) => ({
      key: String(index),
      ...item,
    })) || [];

  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search || '',
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);

  const filterColumns = filterNullColumns(data?.data, column);

  return (
    <ClientsStyle>
      <BreadCrumb arrOfOption={['Clients']} reloaderSize='small' />
      <Modal
        title='Send SMS to Client'
        open={commonState.isModal}
        okText={'Submit'}
        cancelText={'Close'}
        onOk={handleModal}
        onCancel={handleModal}
        footer={null}
      >
        {editInfo && <ClientSmsModal info={editInfo} setNull={setEditInfo} />}
      </Modal>
      <Row justify={'space-between'}>
        <Col lg={4}>
          <Space style={{ marginBottom: '1rem' }}>
            {permission?.[crud.create] && (
              <Link to='/clients/add' state={location.pathname}>
                <Button type='primary'>
                  <PlusOutlined />
                  Add Client
                </Button>
              </Link>
            )}
            <Button
              type='primary'
              onClick={() => {
                fetchExcel({ ...pagination });
              }}
            >
              <FileExcelFilled />
              Excel Report
            </Button>
          </Space>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
              <div style={{ maxWidth: 200 }}>
                <Reload
                  refetch={() => {
                    getAllRefetch();
                  }}
                />
              </div>
            </Row>
          </Form>
        </Col>
      </Row>

      <div>
        <span style={{ marginLeft: '5px', float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count || data?.data?.length || 0}</strong> rows.
        </span>
        <Table
          size='small'
          rowClassName={(item) => getRowHightLightClass(item.client_created_date)}
          bordered
          columns={filterColumns.col}
          dataSource={dataWithKey}
          scroll={{ x: 'max-content' }}
          loading={{
            spinning: dataIsLoading,
            indicator: loadingIndicator,
          }}
          pagination={changePagination(data?.count)}
        />
      </div>
    </ClientsStyle>
  );
};

export default ClientLists;

const ClientsStyle = styled.div``;
